<template>
  <IssueLocationLayout>
    <template #headerBar>
      <HeaderBar :modifiers="['transparent', 'more-height']">
        <router-link
          v-if="backUrl"
          class="button"
          :to="backUrl"
        >
          <LeftArrow class="icon" />
        </router-link>
        <template #right>
          <div
            class="button"
            @click="cancelIssueLocation"
          >
            {{ $t('general.cancel') }}
          </div>
          <FormSubmitButton
            :disabled="!issueLocation"
            @click="storeIssueLocation($route.params.issueId)"
          />
        </template>
      </HeaderBar>
    </template>
    <template #leftContent>
      <div class="location-row">
        <p class="typo-ignore typo-small-title location-type-title">
          {{ $t('shafts.issues.set') }}
        </p>
        <SkeletonElement
          v-if="!shaft"
          :height="40"
          :radius="6"
        />
        <Select
          v-if="shaft"
          ref="select"
          v-model="selectedSet"
          :items="setList"
          :modifiers="[
            'input',
            'base',
            shaft.number_of_sets < 10 ? null : 'short-dropdown',
          ]"
          :placeholder="$t('shafts.3dModel.select_set')"
          @click.passive="checkScroll"
        />
        <StationLink
          v-if="setOnStation"
          :modifiers="['flush']"
          :station="setOnStation"
        />
      </div>

      <div
        v-if="(setElementTypeList.length > 1 && selectedSet) || selectedSet !== null"
        class="location-row"
      >
        <p class="typo-ignore typo-small-title">
          {{ $t('shafts.issues.select_element_type_text') }}
        </p>
        <Select
          v-if="setElementTypeList"
          v-model="selectedElementType"
          :items="setElementTypeList"
          :modifiers="['input', 'base']"
          :placeholder="$t('shafts.issues.select_element')"
        />
      </div>
      <div
        v-if="selectedElementType && selectedElementType !== 'undefined'"
        class="location-row"
      >
        <p class="typo-ignore typo-small-title">
          {{ $t('shafts.issues.select_element_text', { type: singularTypeText }) }}
        </p>
        <Select
          v-model="selectedElement"
          :items="setElementList"
          :modifiers="['input', 'base']"
          :placeholder="$t('shafts.issues.select_element_type', { type: singularTypeText })"
        />
        <p
          v-if="selectedElement !== null"
          class="typo-ignore typo-small-title"
        >
          {{ $t('shafts.issues.place_point_text') }}
        </p>
      </div>
    </template>
    <template #mainContent>
      <Shaft3dModelLayout>
        <template #toolbar>
          <Shaft3dModelOrientation />
          <Shaft3dModelToolbar
            v-model="toolbarValue"
            :disabled="!selectedSet"
            :showSidewallCheckbox="hasSidewalls"
            :sidewallsChecked="sideWallsActive"
            @display-sidewalls="displaySidewalls"
            @update:model-value="selectedAction"
          />
        </template>
        <template #navigation>
          <Shaft3dModelSetNavigation
            :currentSet="selectedSet"
            :isOverlay="hasIssueNavBackRoute"
            :stations="stationList"
            :totalNumbeOfSets="shaft?.number_of_sets"
            @station-name="updateStationName"
          />
        </template>
        <template #scene>
          <Shaft3dModelLoader v-if="loadingSetElements" />
          <div
            v-if="selectedSet && !loadingSetElements"
            ref="sceneContainer"
            class="scene scene-container"
          >
            <div
              v-if="elementHoverName"
              class="scene-container__hover-name"
            >
              {{ elementHoverName }}
            </div>
          </div>
          <EmptyMessage
            v-if="!selectedSet && !loadingSetElements"
            :message="$t('shafts.3dModel.no_set_selection.message')"
            :modifiers="['center', 'grow']"
            :title="$t('shafts.3dModel.no_set_selection.title')"
          />
        </template>
      </Shaft3dModelLayout>
    </template>
  </IssueLocationLayout>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed, watch, nextTick } from 'vue';
import { useRouter, useRoute } from 'vue-router';

// Utils
import {
  ShaftElement,
  IssuePoint,
  handlePointPlacement,
} from '@/functions/shaft3dModel';
import { useShaft3dModel } from '@/use/useShaft3dModel';
import useAjax from '@/functions/use_ajax';
import I18n from 'i18n-js';

// Components
import EmptyMessage from '@/components/generic/empty_message.vue';
import FormSubmitButton from '@/components/form/form_submit_button.vue';
import HeaderBar from '@/components/generic/header_bar.vue';
import IssueLocationLayout from '@/components/shaftsV2/location/issue_location_layout.vue';
import Select from '@/components/form/select.vue';
import Shaft3dModelLoader from '@/components/shaftsV2/shaft_model_loader.vue';
import SkeletonElement from '@/components/generic/skeleton_element.vue';
import Shaft3dModelToolbar from '@/components/shaftsV2/shaft3dModel/shaft_3d_model_toolbar.vue';
import Shaft3dModelOrientation from '@/components/shaftsV2/shaft3dModel/shaft_3d_model_orientation.vue';
import Shaft3dModelSetNavigation from '@/components/shaftsV2/shaft3dModel/shaft_3d_model_set_navigation.vue';
import Shaft3dModelLayout from '@/components/shaftsV2/shaft3dModel/shaft_3d_model_layout.vue';
import StationLink from '@/components/shaftsV2/generic/station_link.vue';

import { useIssueLocationStore } from '@/stores/shafts/issueLocationStore';
import { useIssuesStore } from '@/stores/issues/issuesStore';

// Icons
import LeftArrow from '@/images/arrow-left.svg';

const props = defineProps({
  inspectionId: { type: String, required: false, default: null },
  organisationId: { type: String, required: true },
  shaftId: { type: String, required: true },
  setId: { type: String, required: false, default: null },
  setNumber: { type: String, required: false, default: null },
  /** Use a specific route for the header nav back button */
  issueNavBackRoute: { type: String, required: false, default: null },
});

const issueStore = useIssuesStore();

const {
  initTHREE,
  SCENE,
  clearScene,
  resetCamera,
  elementHoverName,
  focusOnElement,
  selectionByClick,
  handleAddIssuePoint,
  handleClearNewIssuePoint,
  getAllElements,
  setElementMaterialType,
  applyConveyanceStyle,
  displaySidewalls,
  flattenElements,
  sideWallsActive
} = useShaft3dModel();

const shaftIssueLocationStore = useIssueLocationStore();

const router = useRouter();
const route = useRoute();
const ajax = useAjax();
const selectedSet = ref(null);
const selectedElementType = ref(null);
const selectedElement = ref(null);
const shaft = ref(null);
const currentSetElements = ref(null);
const sceneContainer = ref();
const loadingSetElements = ref(false);

const loadingStations = ref(false);
const stationList = ref(null);
const setOnStation = ref(null);

const issueLocation = ref(null);

const select = ref(); // instance of the Select component to access child ref
const toolbarValue = ref(null);
const loading = ref(false);

const baseUrl = `/organisations/${props.organisationId}/shafts_v2/shafts/${props.shaftId}`;

const ENDPOINTS = computed(() => {
  return {
    shaft: baseUrl,
    setElements: `${baseUrl}/sets`,
    stations: `${baseUrl}/stations`,
  };
});

// Checks if a specific route was passed in via props
const hasIssueNavBackRoute = computed(() => {
  return props.issueNavBackRoute !== null;
});

// Default back route - to inspections
const defaultNavBackRoute = computed(() => {
  if (!route.params.issueId || route.params.issueId === 'null' || route.params.issueId === 'undefined') return { name: 'newShaftV2Issue' };
  return { name: 'shaftV2Issue', params: { issueId: route.params.issueId } };
});

// Set back route based on props
const backUrl = computed(() => {
  return hasIssueNavBackRoute.value ? { name: props.issueNavBackRoute } : defaultNavBackRoute.value;
});

const setList = computed(() => {
  let sets = [];
  if (shaft.value) {
    for (let set = 1; set < shaft.value.number_of_sets + 1; set++) {
      sets.push({ value: set, label: set });
    }
  }
  return sets;
});

const setElementTypeList = computed(() => {
  let uniqueList = [
    ...new Set(
      currentSetElements.value?.shaft_element_set.map((item) => {
        return item.shaft_element.element_type;
      })
    ),
  ];

  let list = uniqueList
    .filter((item) => item !== 'skips_and_cages')
    .map((item) => {
      return {
        value: item,
        label: I18n.t(`shafts.element_types.shaft.${item}`),
      };
    });

  list.unshift({
    value: 'undefined',
    label: I18n.t('shafts.issues.undefined'),
  });
  return list;
});

const setElementList = computed(() => {
  return getAllElements(SCENE, selectedElementType.value).map((item) => {
    return {
      value: item.name,
      label: item.name,
    };
  });
});

const singularTypeText = computed(() => {
  return I18n.t(
    `shafts.element_types.type_singular.${selectedElementType.value}`
  ).toLowerCase();
});

const hasSidewalls = computed(() => {
  return currentSetElements.value?.shaft_element_set?.some(
    (e) => e.shaft_element.element_type === 'sidewalls'
  );
});

onMounted(async () => {
  await loadShaft();
  await loadStations();
  await load3dModel();

  if (shaftIssueLocationStore.location?.set || route.params.setId) {
    await updateSetValue();
    setTimeout(() => updateTypeValue(), 1000);
    setTimeout(() => updateNameValue(), 1000);
    setTimeout(() => updatePointValue(), 1000);
  }

  window.addEventListener('dblclick', () => addNewIssuePoint());
});

onUnmounted(() => {
  window.removeEventListener('dblclick', () => addNewIssuePoint());
});

async function updateSetValue() {
  selectedSet.value = shaftIssueLocationStore.location?.set || parseInt(route.params.setId);
}

async function updateTypeValue() {
  selectedElementType.value = shaftIssueLocationStore.location?.elementType;
}

async function updateNameValue() {
  selectedElement.value = shaftIssueLocationStore.location?.element.name;
}

async function updatePointValue() {
  let elementToFocusOn;
  elementToFocusOn = getAllElements(SCENE).find((element) => {
    return element.name === selectedElement.value;
  });

  if (elementToFocusOn) {
    let point = new IssuePoint();

    handlePointPlacement(
      elementToFocusOn,
      point,
      shaft.value.dimensions,
      shaftIssueLocationStore.location.element.position
    );

    elementToFocusOn.add(point);
  }
}

watch(route, () => {
  if(route.params.setId) {
    selectedSet.value = Number(route.params.setId);
  }
});

/**
 * watcher for set dropdown
 */
watch(selectedSet, () => {
  setOnStation.value = null;
  const issueId = route.params?.issueId;
  const hasValidIssueId = Object.hasOwn(route.params, 'issueId') && issueId !== 'undefined' && issueId !== 'null';

  if (!hasIssueNavBackRoute.value) {
    selectedSet.value && router.push({
      name: hasValidIssueId ? 'shaftV2IssueLocationSet' : 'shaftV2NewIssueLocationSet',
      params: { setId: selectedSet.value, issueId },
    });

    !selectedSet.value && router.push({
      name: hasValidIssueId ? 'shaftV2IssueLocationSets' : 'shaftV2NewIssueLocationSets',
      params: { issueId },
    });
  } else {
    const newParams = selectedSet.value ? { setId: selectedSet.value, issueId } : { issueId };
    const isOverview = route.fullPath.includes('overview');
    const overviewPage = route.params.setGroupId ? 'shaftV2IssuesLocationTypeOverviewId' : 'shaftV2IssuesLocationTypeOverview';
    const pageName = isOverview ? overviewPage : 'shaftV2IssuesLocationTypeSet';
    router.push({
      name: pageName,
      params: newParams,
    });
  }

  load3dModel();

  selectedElementType.value = null;
});

/**
 * watcher for element type dropdown
 */
watch(selectedElementType, () => {
  getAllElements(SCENE).forEach((element) => {
    if (element.userData.type === selectedElementType.value) {
      setElementMaterialType(element);
    } else {
      setElementMaterialType(element, 'ghosted');
    }
  });

  applyConveyanceStyle(SCENE);

  selectedElement.value = null;
  resetCamera();
  resetNewIssueLocation();
  displaySidewalls(selectedElementType.value === 'sidewalls');
});

/**
 * watcher for element dropdown
 */
watch(selectedElement, () => {
  let elementToFocusOn = getAllElements(SCENE).find((element) => {
    return element.name === selectedElement.value;
  });

  if (elementToFocusOn) {
    displaySidewalls(elementToFocusOn.userData.type === 'sidewalls');
    focusOnElement(elementToFocusOn);

    getAllElements(SCENE).forEach((element) => {
      setElementMaterialType(element, 'ghosted');
    });

    setElementMaterialType(elementToFocusOn);
  }

  resetNewIssueLocation();
  applyConveyanceStyle(SCENE);
});

/**
 * watcher for selecting an element with the click handler
 */
watch(selectionByClick, async () => {
  if (selectionByClick) {
    selectedElementType.value = selectionByClick.value.userData.type;
    await nextTick();
    selectedElement.value = selectionByClick.value.name;
  }
});

async function loadShaft() {
  if (props.shaftId) {
    let { ok, data } = await ajax(ENDPOINTS.value.shaft);

    if (ok) {
      shaft.value = data;
    }
  }
}

async function loadStations() {
  loadingStations.value = true;
  let { ok, data } = await ajax(ENDPOINTS.value.stations);

  if (ok) {
    stationList.value = data;
    loadingStations.value = false;
  }
}

async function load3dModel() {
  await clearScene(SCENE);
  await loadSetElements();
  await initTHREE(sceneContainer.value, shaft.value.dimensions);
  renderElements();
}

async function loadSetElements() {
  if (!selectedSet.value && !props.setId) return;
  loadingSetElements.value = true;

  let { ok, data } = await ajax(`${ENDPOINTS.value.setElements}/${selectedSet.value || props.setId}`);

  if (ok) {
    currentSetElements.value = data;
  }

  loadingSetElements.value = false;
}

function addNewIssuePoint() {
  issueLocation.value = handleAddIssuePoint(shaft.value.dimensions);
}

function storeIssueLocation(issueId) {
  const selectedElementData = currentSetElements.value?.shaft_element_set.find((element) => element?.shaft_element?.name === selectedElement.value);
  const issuePoint = issueLocation.value?.parent?.children?.find((child) => child?.name === 'new point');

  /**
   * If issueId is available we can directly update the location,
   * otherwise it saves the location to state
   */
  if (issueId !== 'null' && issueId !== 'undefined') {
    selectedElementData.shaft_element.position = issuePoint?.position;
    updateActivity({
      issue: {
        location_type: 'ShaftsV2::ShaftLocation',
        location_attributes: {
          id: shaftIssueLocationStore.formData.location.id,
          set_number: selectedElementData.set_number,
          shaft_element_set_id: selectedElementData.id,
          position: selectedElementData.shaft_element.position,
        },
      },
    });
  } else {
    shaftIssueLocationStore.$patch({
      location: {
        element: {
          name: selectedElement.value,
          position: issueLocation.value.position,
          set_id: selectedElementData.id,
        },
        elementType: selectedElementType.value,
        set: selectedSet.value,
        type: 'shaft',
      },
    });

    router.push(backUrl.value);
  }
}

function renderElements() {
  currentSetElements.value?.shaft_element_set.forEach((set) => {
    SCENE.add(new ShaftElement(set.shaft_element, shaft.value));
  });

  displaySidewalls(shaftIssueLocationStore.location?.elementType === 'sidewalls');
}

function checkScroll() {
  if (!selectedSet.value) {
    select.value.dropdownContainerRef.scrollTop = 0;
  }
}

function resetNewIssueLocation() {
  if (issueLocation.value) {
    handleClearNewIssuePoint();
    issueLocation.value = null;
  }
}

function cancelIssueLocation() {
  if (selectedSet.value) {
    selectedSet.value = null;
    selectedElementType.value = null;
    selectedElement.value = null;
  } else {
    router.push(backUrl.value);
  }
}

// TODO: extract toolbar function into reusable functions
/**
 * Gets the selected toolbar option and
 * calls the relevant method to update the view
 */
function selectedAction(event) {
  flattenElements(event);
  event === 'reset' && resetCamera();
}

/**
 *
 * Save the updated location attributes
 */
async function updateActivity(attributes) {
  loading.value = true;

  if (!route.params.issueId || route.params.issueId === 'null') return router.push(backUrl.value);

  const baseUrl = `/organisations/${props.organisationId}/shafts_v2/shafts/${props.shaftId}`;
  const inspectionUrl = `${baseUrl}/inspections/${props.inspectionId}`;
  const issueUrl = `${inspectionUrl}/issues/${route.params.issueId}`;
  let { ok, data } = await ajax(issueUrl, {
    method: 'PATCH',
    body: attributes,
  });

  if (ok) {
    issueStore.updateIssue({...data, location_update: true});
    router.push(backUrl.value);
  }
  loading.value = false;
}

function updateStationName(data) {
  setOnStation.value = JSON.parse(data);
}
</script>

<style scoped lang="scss">
@import '@/stylesheets/global';
.location-row {
  &:not(:last-of-type) {
    margin-bottom: bu(3.75);
  }

  p {
    margin: 0 0 bu(2) 0;
    line-height: bu(2.5);

    &:last-child {
      margin-top: bu(2);
    }
  }
}
</style>
